/** @format */

import React, { RefObject, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// #region Types
export interface ExpandingContainerPropsType {
  animate?: boolean;
  visible?: boolean;
  duration?: number;
  minHeight?;
  events?: {
    onChange?: (from: boolean | undefined, to: boolean | undefined) => void;
    onOpen?: () => void;
    onClose?: () => void;
    onTransitionStart?: () => void;
    onTransitionEnd?: () => void;
  };
}

export interface ExtraInfoRowContainerElementPropsType {
  visible?: boolean;
  minHeight?: number;
}
// #endregion

// #region Elements
export const ExpandingContainerElement = styled.div<ExtraInfoRowContainerElementPropsType>`
  height: ${({ visible, minHeight }) => (visible ? 'auto' : minHeight)};
  overflow: hidden;
`;
// #endregion

// #region Components
const animateVisibilityChange = (
  visible: boolean | undefined,
  localVisible: boolean | undefined,
  element: HTMLTableCellElement | null,
  duration = 300,
  minHeight = 0,
  callback?: () => void
): ReturnType<typeof setTimeout> => {
  let callbackTimeout: ReturnType<typeof setTimeout> = undefined;
  if (visible && !localVisible && element) {
    element.style.height = 'auto';
    window.requestAnimationFrame(() => {
      const height = element.getBoundingClientRect().height;
      element.style.height = `${minHeight}px`;
      element.style.transition = `height ${duration}ms`;
      window.requestAnimationFrame(() => {
        element.style.overflow = 'hidden';
        element.style.height = `${height}px`;
        if (callback)
          callbackTimeout = setTimeout(() => {
            callback();
            element.style.height = 'auto';
          }, duration * 1.1);
      });
    });
  } else if (!visible && localVisible && element) {
    const height = element.getBoundingClientRect().height;
    element.style.height = `${height}px`;
    element.style.transition = `height ${duration}ms`;
    window.requestAnimationFrame(() => {
      element.style.overflow = 'hidden';
      element.style.height = `${minHeight}px`;
      if (callback) callbackTimeout = setTimeout(callback, duration * 1.1);
    });
  }
  return callbackTimeout;
};

export const ExpandingContainer: React.FunctionComponent<ExpandingContainerPropsType> =
  ({ visible, animate, events, minHeight = 0, duration, children }) => {
    const [initialHeight] = useState<string>(visible ? 'auto' : minHeight);
    const [localVisible, setLocalVisible] = useState<boolean | undefined>(
      visible
    );
    const [animationEndTimeout, setAnimationEndTimeout] =
      useState<ReturnType<typeof setTimeout>>(undefined);
    const elementRef: RefObject<HTMLTableCellElement> = useRef(null);

    useEffect((): (() => void) => {
      if (visible !== localVisible) {
        if (events) {
          events.onChange && events.onChange(localVisible, visible);
          visible && !localVisible && events.onOpen && events.onOpen();
          !visible && localVisible && events.onClose && events.onClose();
        }
        if (animate) {
          clearTimeout(animationEndTimeout);
          if (events && events.onTransitionStart) events.onTransitionStart();
          const element = elementRef && elementRef.current;
          setAnimationEndTimeout(
            animateVisibilityChange(
              visible,
              localVisible,
              element,
              duration,
              minHeight,
              () => {
                if (events && events.onTransitionEnd) events.onTransitionEnd();
                setLocalVisible(visible);
              }
            )
          );
        } else {
          setLocalVisible(visible);
        }
      }
      return () => clearTimeout(animationEndTimeout);
    }, [
      visible,
      localVisible,
      animate,
      events,
      duration,
      minHeight,
      animationEndTimeout,
    ]);

    return (
      <ExpandingContainerElement
        visible={visible}
        ref={elementRef}
        style={{ height: initialHeight }}
      >
        {children}
      </ExpandingContainerElement>
    );
  };
// #endregion
